import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSubmitData } from "../../../hooks/useSubmitData";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormLayout from '../../Generic/FormLayout';
import FormInput from '../../Generic/FormInput';
import * as yup from "yup";
import FormSelect from '../../Generic/FormSelect';

const Add = () => {
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    useSetMenu(mainStore.CriteriaValueMenuType, mainStore.CriteriaValueMenuNo);
    const onError = (errors, e) =>
        enqueueSnackbar(t("onErrorSubmit"), { variant: "error" });
    
    // State for batch values input
    const [batchValues, setBatchValues] = useState("");
    const [valuesArray, setValuesArray] = useState([]);

    // Move the useSubmitData hook call to the top level of the component
    const sendData = useSubmitData(axiosPrivate, "/CriteriaValues", "/CriteriaValue/List");

    const schema = yup.object({
        subcategoryId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        criteriaId: yup.string().required(t("selectRequired")),
        // The value field is not required when using batch mode
        value: yup
            .string()
            .max(250, t('maxLength', { max: 250 })),
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    // Modified to handle batch submissions
    const onSubmit = async (data) => {
        if (valuesArray.length === 0 && !data.value) {
            enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
            return;
        }

        // If there are batch values, use those
        if (valuesArray.length > 0) {
            try {
                // Create a request that includes all values
                const batchRequest = {
                    criteriaId: parseInt(data.criteriaId),
                    panelId: parseInt(data.panelId),
                    categoryId: parseInt(data.categoryId),
                    subcategoryId: parseInt(data.subcategoryId),
                    values: valuesArray
                };
                
                await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
                enqueueSnackbar(t("batchCreateSuccess"), { variant: "success" });
                navigate("/CriteriaValue/List");
            } catch (error) {
                enqueueSnackbar(t("batchCreateError"), { variant: "error" });
                console.error(error);
            }
        } else {
            // Single value submission using the existing hook
            sendData(data);
        }
    };
    
    const onCancel = () => navigate(-1);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [t]);

    // Process the batch input into an array of values
    useEffect(() => {
        if (batchValues.trim()) {
            // Split by newlines, commas, or semicolons
            const values = batchValues
                .split(/[\n,;]/)
                .map(val => val.trim())
                .filter(val => val.length > 0 && val.length <= 250);
            
            setValuesArray(values);
        } else {
            setValuesArray([]);
        }
    }, [batchValues]);

    //#region Criteria Select
    const [panels, setPanels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [criterias, setCriterias] = useState([]);

    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedCriteria, setSelectedCriteria] = useState(null);

    async function loadData() {
        try {
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
        } catch (err) {
            enqueueSnackbar(t("loadDataError"), { variant: "error" });
        }
    }
    const handleCriteriaChange = selectedOption => {
        setValue("criteriaId", selectedOption.value, { shouldValidate: true });
        setSelectedCriteria(selectedOption);
    };

    const handleSubcategoryChange = async selectedOption => {
        setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCriterias([]);
            } else {
                setCriterias(response.data);
            }
            setSelectedCriteria(null);
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }
            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setCriterias([]);

            setValue("subcategoryId", null, { shouldValidate: true });
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSubcategories([]);
            setCriterias([]);

            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setSelectedCriteria(null);

            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });
            setValue("criteriaId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };
    //#endregion

    return (
        <FormLayout title={t("criteriaValueCreateTitle")} onSubmit={handleSubmit(onSubmit, onError)} onCancel={onCancel}>
            <FormSelect label="panel" name="panelId" vaule={selectedPanel} register={register} errors={errors} colSize={4} options={panels} onChange={handlePanelChange} />
            <FormSelect label="category" name="categoryId" value={selectedCategory} register={register} errors={errors} colSize={4} options={categories} onChange={handleCategoryChange} />
            <FormSelect label="subcategory" name="subcategoryId" value={selectedSubcategory} register={register} errors={errors} colSize={4} options={subcategories} onChange={handleSubcategoryChange} />
            <FormSelect label="criteria" name="criteriaId" value={selectedCriteria} register={register} errors={errors} options={criterias} onChange={handleCriteriaChange} />
            
            <div className="col-md-12 mb-3">
                <h5>{t("singleOrBatchMode")}</h5>
                <p className="text-muted">{t("chooseBatchModeInstructions")}</p>
            </div>

            {/* Single value input */}
            <FormInput 
                label="criteriaValue" 
                name="value" 
                register={register} 
                errors={errors} 
            />
            
            {/* Batch values input */}
            <div className="col-md-12 mb-3">
                <label className="form-label">{t("batchValues")}</label>
                <textarea
                    className="form-control"
                    rows={5}
                    placeholder={t("enterMultipleValues")}
                    value={batchValues}
                    onChange={(e) => setBatchValues(e.target.value)}
                />
                <div className="form-text">
                    {t("batchValuesHelp")}
                </div>
                
                {/* Preview of parsed values */}
                {valuesArray.length > 0 && (
                    <div className="mt-3">
                        <h6>{t("valuePreview")} ({valuesArray.length})</h6>
                        <div className="border p-2 bg-light">
                            {valuesArray.map((val, index) => (
                                <span key={index} className="badge bg-secondary me-1 mb-1">{val}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </FormLayout>
    );
};

export default Add;